// Fachregeln der Kampagnen an einer Stelle (Bestand Planung Recherche/18 Teil C; neuratek/akquise/ablauf.md „Anruf“, „Kaltmail“,
// „Rückmeldungen und Antworten“). Server und Oberfläche nutzen dieselben Werte und Rechenwege.

// AK-045: Anruf-Werte; offene zählen als Versuch, abschließende beenden die Zeile
export const ANRUF_OFFEN = ['nicht erreicht', 'Mailbox', 'später nochmal'] as const;
export const ANRUF_ENDE = ['Termin', 'kein Interesse', 'hat schon jemanden', 'falsche Nummer', 'nicht mehr anrufen'] as const;
export const ANRUF_WERTE = [...ANRUF_OFFEN, 'Unterlagen per Mail', ...ANRUF_ENDE] as const;
export type AnrufWert = (typeof ANRUF_WERTE)[number];

// AK-048: Platzhalter der Vorlagen
export const PLATZHALTER = ['Anrede', 'Firma', 'Ansprechpartner', 'Vorname', 'Nachname', 'Stadt', 'Kategorie', 'Aufhänger', 'Förderung', 'Terminlink'] as const;

// AK-036 (für KI ohne Varianten: „alle Firmen“)
export const VARIANTEN = ['keine Website', 'Website nicht erreichbar', 'Website verbesserbar', 'alle Firmen', 'Nachfass 1'] as const;
export type Variante = (typeof VARIANTEN)[number];
/** Nachfass 1 geht als Antwort auf die Erstmail im selben Verlauf (Jarvis neuratek/akquise/kaltmails-website.md) */
export const NACHFASS = 'Nachfass 1';

/** Aufhänger je Fall (Leon 2026-10-29, kaltmails-website.md); schließt an „…, waren wir so frei“ an */
export const AUFHAENGER: Record<string, string> = {
  'Website nicht erreichbar': 'Da Ihre aktuelle Website nicht erreichbar ist',
  'keine Website': 'Da Sie aktuell gar keine Website haben und somit über das Internet nicht auffindbar sind',
  allgemein: 'Da eine Website auf echtem Agenturniveau meist hohe Kosten und viel eigenen Aufwand bedeutet'
};
export const aufhaengerFuer = (angebot: string, variante: string) => angebot === 'KI' ? '' : AUFHAENGER[variante] ?? AUFHAENGER.allgemein;

// AK-037: 13 Einordnungen
export const EINORDNUNG = ['Termin gewünscht', 'Frage', 'Interesse', 'Unterlagen gewünscht', 'später melden', 'Weiterleitung', 'kein Interesse', 'Abmeldung', 'Beschwerde oder Drohung', 'Abwesenheitsnotiz', 'automatische Antwort', 'unzustellbar', 'Sonstiges'] as const;
export type Einordnung = (typeof EINORDNUNG)[number];
/** Einordnungen, auf die eine Antwort geschrieben wird (Rest: keine Antwort, AK-178). */
export const MIT_ANTWORT: readonly Einordnung[] = ['Termin gewünscht', 'Frage', 'Interesse', 'Unterlagen gewünscht', 'später melden', 'Weiterleitung', 'Sonstiges'];

export const KAMPAGNEN_ZUSTAENDE = ['Entwurf', 'läuft', 'pausiert', 'beendet'] as const;
export const MAIL_ZUSTAENDE = ['vorbereitet', 'freigegeben', 'versendet', 'unzustellbar', 'verworfen'] as const;

// Statusvorrang (Jarvis): ein niedrigerer Status überschreibt nie einen höheren
const VORRANG = ['Kunde', 'gesperrt', 'Termin', 'in Kontakt', 'Rückmeldung offen'];
export function darfUeberschreiben(alt: string | null, neu: string): boolean {
  const a = alt ? VORRANG.indexOf(alt) : -1, n = VORRANG.indexOf(neu);
  if (a < 0) return true;
  return n >= 0 && n <= a;
}

/* ---------- Anrufliste (AK-132, AK-133) ---------- */

export interface AnrufZeileKern { e: (string | null)[]; gesperrt: string | null; termin: string | null; nachfassenAb: string | null }
export interface AnrufStand { status: string; sub: string; grp: 'offen' | 'nach' | 'termin' | 'zu'; fertig: boolean; ton: string }

export function anrufStand(z: AnrufZeileKern, versuche = 3): AnrufStand {
  if (z.gesperrt) return { status: 'Rückmeldung offen', sub: z.gesperrt, grp: 'zu', fertig: true, ton: 'warn' };
  let offen = 0, unterlagen = false;
  for (const v of z.e) {
    if (!v) continue;
    if ((ANRUF_ENDE as readonly string[]).includes(v)) {
      if (v === 'Termin') return { status: 'Termin', sub: z.termin ? `Termin ${z.termin}` : 'Termin eingetragen', grp: 'termin', fertig: true, ton: 'ok' };
      if (v === 'nicht mehr anrufen') return { status: 'gesperrt', sub: 'Nummer auf der Sperrliste', grp: 'zu', fertig: true, ton: 'neutral' };
      if (v === 'falsche Nummer') return { status: 'wartet', sub: 'falsche Nummer; der nächste Telefonweg kommt als neue Zeile', grp: 'zu', fertig: true, ton: 'neutral' };
      return { status: 'kein Interesse', sub: v, grp: 'zu', fertig: true, ton: 'neutral' };
    }
    if (v === 'Unterlagen per Mail') { unterlagen = true; continue; }
    if (!unterlagen) offen++;
  }
  if (unterlagen) return { status: 'Nachfassen', sub: z.e[3] ? `Nachfassen: ${z.e[3]}` : `Unterlagen gesendet · Nachfassen ab ${z.nachfassenAb ?? 'bald'}`, grp: 'nach', fertig: false, ton: 'accent' };
  if (offen >= versuche) return { status: 'keine Rückmeldung', sub: `${versuche} Versuche ohne Ergebnis`, grp: 'zu', fertig: true, ton: 'neutral' };
  return { status: 'Anrufliste', sub: offen ? `${offen} von ${versuche} Versuchen` : 'noch nicht angerufen', grp: 'offen', fertig: false, ton: 'neutral' };
}

/** Status des Angebots aus dem Stand der Zeile (AK-133). */
export function statusAusStand(s: AnrufStand): string {
  return s.status === 'Anrufliste' || s.status === 'Nachfassen' || s.status === 'Termin' || s.status === 'kein Interesse' || s.status === 'keine Rückmeldung' || s.status === 'gesperrt' || s.status === 'wartet' ? s.status : 'Rückmeldung offen';
}

/** In welches Feld der nächste Eintrag gehört: Nachfassen, sobald Unterlagen gesendet sind, sonst der erste freie Versuch. */
export function naechsterSlot(z: AnrufZeileKern, versuche = 3): number {
  const s = anrufStand(z, versuche);
  if (s.grp === 'nach') return 3;
  if (s.fertig) return -1;
  const i = z.e.slice(0, versuche).findIndex(x => !x);
  return i;
}

/* ---------- Vorlagen ---------- */

/** Platzhalter füllen (AK-152): Zeilen mit leerem Platzhalter fallen weg, leere Zeilen werden geglättet. */
export function fuellen(vorlage: string, werte: Partial<Record<string, string>>): { text: string; leer: string[] } {
  const leer = new Set<string>();
  // {mit Förderung: … | ohne: …}: Satz je nachdem, ob die Firma förderfähig ist (Ausschlussliste EN-092)
  vorlage = vorlage.replace(/\{mit Förderung:([^|}]*)\|\s*ohne:([^}]*)\}/g, (_m, mit: string, ohne: string) => (werte['förderfähig'] === 'nein' ? ohne : mit).trim());
  const zeilen = vorlage.split('\n').filter(zeile => {
    let ohneWert = false;
    zeile.replace(/\{([^}]+)\}/g, (_m, p: string) => { if (!werte[p]) { ohneWert = true; leer.add(p); } return ''; });
    return !ohneWert;
  }).map(zeile => zeile.replace(/\{([^}]+)\}/g, (_m, p: string) => werte[p] ?? ''));
  const text = zeilen.filter((l, i, a) => l.trim() !== '' || (i > 0 && a[i - 1].trim() !== '')).join('\n').trim();
  return { text, leer: [...leer] };
}

/** Änderungsgrad wie im Bestand (AK-185): 100 − 200 · gemeinsame Wörter / (Wörter A + Wörter B), Wörter über 2 Zeichen. */
export function aenderung(a: string, b: string): number {
  const w = (s: string) => s.toLowerCase().split(/[^a-zäöüß0-9]+/).filter(x => x.length > 2);
  const A = w(a), B = w(b);
  if (!A.length && !B.length) return 0;
  const m = new Map<string, number>();
  A.forEach(x => m.set(x, (m.get(x) ?? 0) + 1));
  let g = 0;
  B.forEach(x => { const n = m.get(x); if (n) { g++; m.set(x, n - 1); } });
  return Math.max(0, Math.round(100 - (200 * g) / (A.length + B.length)));
}
export const ALS_BEISPIEL_BIS = 40;

/** Variante der Kaltmail je Firma (AK-149). */
export function varianteVon(angebot: string, websiteTyp: string | null, impressumMarker: string | null, websiteZustand: string | null): Variante {
  if (angebot === 'KI') return 'alle Firmen';
  if (websiteTyp === 'keine Website vorhanden') return 'keine Website';
  if ((impressumMarker ?? '').toLowerCase().includes('nicht erreichbar') || (websiteZustand ?? '').toLowerCase().includes('nicht erreichbar')) return 'Website nicht erreichbar';
  return 'Website verbesserbar';
}

/* ---------- Anrede (Leon 2026-10-22): abgeleitet, nicht neutral ---------- */

export interface AnredeErgebnis { anrede: string; vorname: string; nachname: string; hinweis: string | null }

/**
 * ① Vornamenliste mit Geschlecht · ② (Jarvis, kleinstes Modell, später) · ③ „Sehr geehrte Damen und Herren,“ nur ohne Vornamen.
 * Unbekannter Vorname: Hinweis in der Stichprobe, die Mail wartet nicht.
 */
export function anredeAbleiten(ansprechpartner: string | null, vornamen: ReadonlyMap<string, 'w' | 'm'>): AnredeErgebnis {
  const teile = (ansprechpartner ?? '').replace(/\b(Dr|Prof|Dipl|Ing|med|rer|nat)\.?\s*/gi, '').trim().split(/\s+/).filter(Boolean);
  const nachname = teile.length > 1 ? teile[teile.length - 1] : '';
  const vorname = teile.length > 1 && teile[0].replace(/\.$/, '').length > 1 ? teile[0] : '';
  if (!nachname) return { anrede: 'Sehr geehrte Damen und Herren,', vorname: '', nachname: '', hinweis: null };
  if (!vorname) return { anrede: 'Sehr geehrte Damen und Herren,', vorname: '', nachname, hinweis: null };
  const g = vornamen.get(vorname.toLowerCase().split('-')[0]);
  if (g === 'w') return { anrede: `Sehr geehrte Frau ${nachname},`, vorname, nachname, hinweis: null };
  if (g === 'm') return { anrede: `Sehr geehrter Herr ${nachname},`, vorname, nachname, hinweis: null };
  return { anrede: `Guten Tag ${vorname} ${nachname},`, vorname, nachname, hinweis: `Anrede unklar: Vorname „${vorname}“ steht nicht in der Vornamenliste; bitte prüfen oder die Mail verwerfen.` };
}

/* ---------- Versandfenster, Feiertage, Antwortzeit ---------- */

export const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

/** Ortszeit Berlin: Datum JJJJ-MM-TT, Wochentag, Stunde. */
export function berlin(d: Date): { datum: string; tag: (typeof WOCHENTAGE)[number]; stunde: number } {
  const teile = Object.fromEntries(new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short' })
    .formatToParts(d).map(t => [t.type, t.value]));
  const tag = (teile.weekday as string).replace('.', '').slice(0, 2) as (typeof WOCHENTAGE)[number];
  return { datum: `${teile.year}-${teile.month}-${teile.day}`, tag, stunde: Number(teile.hour) };
}

function ostern(jahr: number): Date {
  const a = jahr % 19, b = Math.floor(jahr / 100), c = jahr % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31), tag = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(jahr, monat - 1, tag));
}
const iso = (d: Date) => d.toISOString().slice(0, 10);
const plus = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

/** Gesetzliche Feiertage: bundesweite und die des Bundeslands (ohne nur örtliche wie Augsburger Friedensfest). */
export function feiertage(jahr: number, land: string | null): Set<string> {
  const o = ostern(jahr), t = (m: number, d: number) => `${jahr}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const s = new Set([t(1, 1), iso(plus(o, -2)), iso(plus(o, 1)), t(5, 1), iso(plus(o, 39)), iso(plus(o, 50)), t(10, 3), t(12, 25), t(12, 26)]);
  const L = land ?? '';
  if (['Baden-Württemberg', 'Bayern', 'Sachsen-Anhalt'].includes(L)) s.add(t(1, 6));
  if (['Berlin', 'Mecklenburg-Vorpommern'].includes(L)) s.add(t(3, 8));
  if (['Baden-Württemberg', 'Bayern', 'Hessen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland'].includes(L)) s.add(iso(plus(o, 60)));
  if (L === 'Saarland') s.add(t(8, 15));
  if (L === 'Thüringen') s.add(t(9, 20));
  if (['Brandenburg', 'Bremen', 'Hamburg', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'].includes(L)) s.add(t(10, 31));
  if (['Baden-Württemberg', 'Bayern', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland'].includes(L)) s.add(t(11, 1));
  if (L === 'Sachsen') { const n23 = new Date(Date.UTC(jahr, 10, 23)); s.add(iso(plus(n23, -((n23.getUTCDay() + 4) % 7) || -7))); }
  return s;
}

/** Datum (JJJJ-MM-TT) n Werktage nach ab: ohne Wochenende und Feiertage des Lands */
export function werktageSpaeter(n: number, ab: Date, land: string | null = null): string {
  let d = new Date(Date.UTC(ab.getUTCFullYear(), ab.getUTCMonth(), ab.getUTCDate()));
  for (let i = 0; i < n;) {
    d = plus(d, 1);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !feiertage(d.getUTCFullYear(), land).has(iso(d))) i++;
  }
  return iso(d);
}

export interface Fenster { tage: string[]; von: number; bis: number; feiertage: boolean }

/** Darf jetzt gesendet werden (Versandtage, volle Stunden von bis ausschließlich bis, Feiertage des Lands)? */
export function imFenster(jetzt: Date, f: Fenster, land: string | null = null): boolean {
  const b = berlin(jetzt);
  if (!f.tage.includes(b.tag) || b.stunde < f.von || b.stunde >= f.bis) return false;
  return !(f.feiertage && feiertage(Number(b.datum.slice(0, 4)), land).has(b.datum));
}

/**
 * Antwortzeit (Leon 2026-10-21): nie binnen Sekunden, sondern zufällig 20 bis 90 Minuten nach dem Eingang, nur im Fenster;
 * fällt das außerhalb, am nächsten Werktag zwischen 8 und 10 Uhr zu zufälliger Zeit.
 */
export function antwortZeitpunkt(eingang: Date, jetzt: Date, f: Fenster & { antVon: number; antBis: number }, zufall: () => number, land: string | null = null): Date {
  const ziel = new Date(eingang.getTime() + (f.antVon + zufall() * Math.max(0, f.antBis - f.antVon)) * 60000);
  const frueh = ziel > jetzt ? ziel : new Date(jetzt.getTime() + 60000);
  if (imFenster(frueh, f, land)) return frueh;
  // nächster Werktag im Fenster: Tag für Tag weiter, 8 Uhr Ortszeit plus 0–120 Minuten
  for (let n = 0; n < 14; n++) {
    const tag = new Date(frueh.getTime() + n * 86400000);
    const b = berlin(tag);
    const acht = new Date(`${b.datum}T08:00:00`);
    const versatz = acht.getTime() - new Date(acht.toLocaleString('en-US', { timeZone: 'Europe/Berlin' })).getTime();
    const start = new Date(acht.getTime() + versatz + Math.round(zufall() * 120) * 60000);
    if (start > frueh && imFenster(start, f, land)) return start;
  }
  return frueh;
}

/* ---------- Anlaufplan der Postfächer (AK-147) ---------- */

export const ANLAUFPLAN: { woche: string; ab: number; je: number; text: string }[] = [
  { woche: '1 und 2', ab: 1, je: 0, text: 'nur Aufwärmen' }, { woche: '3', ab: 3, je: 10, text: '' }, { woche: '4', ab: 4, je: 20, text: '' }, { woche: '5 und danach', ab: 5, je: 30, text: '' }
];
/** Kaltmails je Postfach und Tag nach der Stufe des Anlaufplans (3, 4 oder 5). */
export const limitDerStufe = (stufe: number) => (ANLAUFPLAN.slice().reverse().find(p => stufe >= p.ab) ?? ANLAUFPLAN[0]).je;

/* ---------- Einstellungen: Standardwerte (Prototyp CFG, Bestand AK-059 bis AK-073) ---------- */

export const EINSTELLUNG_STANDARD = {
  mailAktiv: false, tage: ['Mo', 'Di', 'Mi', 'Do', 'Fr'] as string[], von: 8, bis: 17, feiertage: true, proTag: 180, ohneFreigabe: false, stichprobe: 5,
  antVon: 20, antBis: 90, auto: true, anlaufStufe: 4, warteAnruf: 10, rueckkehr: 7, versuche: 3, zeilen: 50, spaeter: 90, eingeschlafen: 14,
  karenz: 15, nachfassen: 5, kennung: '', bounceGrenze: 3, beschwerde: 1, sendefehler: true, pruefen: true, absender: '', minEw: 2000, deckel: 5
};
export type Einstellungen = typeof EINSTELLUNG_STANDARD;
export type EinstellungSchluessel = keyof Einstellungen;

// Bereich, Bezeichnung und Grenzen je Schlüssel (für Protokoll, Rücknahme und Prüfung)
export const EINSTELLUNG_INFO: Record<EinstellungSchluessel, { bereich: string; name: string; min?: number; max?: number }> = {
  mailAktiv: { bereich: 'Versand und Anrufe', name: 'Mailversand' }, tage: { bereich: 'Versand und Anrufe', name: 'Versandtage' },
  von: { bereich: 'Versand und Anrufe', name: 'Versandzeit von', min: 0, max: 23 }, bis: { bereich: 'Versand und Anrufe', name: 'Versandzeit bis', min: 1, max: 24 },
  feiertage: { bereich: 'Versand und Anrufe', name: 'An Feiertagen nicht senden' }, proTag: { bereich: 'Versand und Anrufe', name: 'Höchstens Mails am Tag', min: 0, max: 5000 },
  ohneFreigabe: { bereich: 'Versand und Anrufe', name: 'Ohne Freigabe senden' }, stichprobe: { bereich: 'Versand und Anrufe', name: 'Stichprobe je Charge', min: 1, max: 50 },
  antVon: { bereich: 'Versand und Anrufe', name: 'Antwortzeit von', min: 5, max: 600 }, antBis: { bereich: 'Versand und Anrufe', name: 'Antwortzeit bis', min: 5, max: 600 },
  auto: { bereich: 'Anlaufplan', name: 'Automatisch anheben' }, anlaufStufe: { bereich: 'Anlaufplan', name: 'Stufe des Anlaufplans', min: 1, max: 5 },
  warteAnruf: { bereich: 'Anrufe und Wiedervorlagen', name: 'Wartezeit bis zum Anruf', min: 1, max: 60 }, rueckkehr: { bereich: 'Anrufe und Wiedervorlagen', name: 'Nach einer Abwesenheitsnotiz', min: 0, max: 60 },
  versuche: { bereich: 'Anrufe und Wiedervorlagen', name: 'Anrufversuche', min: 1, max: 3 }, zeilen: { bereich: 'Anrufe und Wiedervorlagen', name: 'Offene Zeilen je Anrufliste', min: 5, max: 200 },
  spaeter: { bereich: 'Anrufe und Wiedervorlagen', name: '„später melden“ ohne Datum', min: 1, max: 365 }, eingeschlafen: { bereich: 'Anrufe und Wiedervorlagen', name: 'Gespräch eingeschlafen', min: 1, max: 90 },
  karenz: { bereich: 'Unterlagen', name: 'Wartezeit vor dem Versand', min: 0, max: 240 },
  nachfassen: { bereich: 'Unterlagen', name: 'Nachfassen nach', min: 1, max: 60 }, kennung: { bereich: 'Eingang und Filter', name: 'Aufwärm-Kennung' },
  bounceGrenze: { bereich: 'Schutzschalter', name: 'Bounces je Postfach und Domain', min: 0.5, max: 20 }, beschwerde: { bereich: 'Schutzschalter', name: 'Spam-Beschwerden', min: 0, max: 20 },
  sendefehler: { bereich: 'Schutzschalter', name: 'Sendefehler eines Postfachs' }, pruefen: { bereich: 'Schutzschalter', name: 'Adresse vor dem Versand prüfen' },
  absender: { bereich: 'Absender und Signatur', name: 'Absender der Kaltmails' }, minEw: { bereich: 'Städte', name: 'Mindestens Einwohner' }, deckel: { bereich: 'Mailmuster', name: 'Höchstens Prüfungen je Firma', min: 1, max: 16 }
};

// Gruppen und Bereiche der Seite Einstellungen (Prototyp SET_GROUPS)
export const EINSTELLUNG_GRUPPEN: [string, [string, string][]][] = [
  ['Ansprache', [['versand', 'Versand und Anrufe'], ['absender', 'Absender und Signatur'], ['termine', 'Termine und Kalender'], ['unterlagen', 'Unterlagen'], ['schutz', 'Schutzschalter'], ['eingang', 'Eingang und Filter']]],
  ['Beschaffung', [['begriffe', 'Suchbegriffe'], ['staedte', 'Städte']]],
  ['Anreicherung', [['kategorien', 'Kategorien'], ['foerderung', 'Förderung'], ['portale', 'Portale'], ['pfade', 'Impressum-Pfade'], ['muster', 'Mailmuster']]],
  ['Allgemein', [['dienste', 'Dienste und Guthaben'], ['sperrliste', 'Sperrliste'], ['opener', 'Opener'], ['aenderungen', 'Änderungen']]]
];

// Eingang (AK-040, AK-056)
export const SYSTEM_ABSENDER = ['microsoft.com', 'microsoftonline.com', 'office365.com', 'office.com', 'onmicrosoft.com', 'instantly.ai'];
export const FREEMAIL = ['web.de', 'gmx.de', 'gmx.net', 'gmail.com', 'googlemail.com', 't-online.de', 'freenet.de', 'outlook.de', 'outlook.com', 'hotmail.de', 'hotmail.com', 'yahoo.de', 'yahoo.com', 'aol.com', 'icloud.com', 'me.com'];

/** Bounce erkennen (AK-173). */
export const istBounce = (von: string, betreff: string | null) =>
  /^postmaster@/i.test(von) || /mailer-daemon/i.test(von) || /undeliverable|unzustellbar|delivery status notification|mail delivery failed/i.test(betreff ?? '');

export const domainVon = (adresse: string) => adresse.toLowerCase().split('@')[1] ?? '';
export const passtDomain = (domain: string, liste: readonly string[]) => liste.some(d => domain === d || domain.endsWith('.' + d));

/** Kennung wie im Prototyp. */
export const mailKennung = (id: number) => 'M-' + String(id).padStart(6, '0');
export const rueckKennung = (id: number) => 'R-' + String(id).padStart(6, '0');
export const vorlageKennung = (id: number) => 'V-' + String(id).padStart(3, '0');
